/**
 * Admin → Raids → Manual profile.
 *
 * The saved profile is the DEFAULT for the admin Summon button. The Summon
 * panel below pre-fills from it and can change anything for one raid without
 * saving — the two actions are deliberately worded differently, because a
 * per-summon override silently becoming the new default is the single easiest
 * bug to ship here.
 */
import { useEffect, useState } from "react";
import { Swords, Zap, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { RaidProfileEditor } from "@/components/admin/RaidProfileEditor";
import { PassivePicker } from "@/components/admin/PassivePicker";
import { PalPicker, usePalPool } from "@/components/admin/PalPicker";
import { PlayerPicker } from "@/components/admin/PlayerPicker";

const NUM = "input input-sm w-full text-[11px]";

export default function AdminRaidsManual() {
  const toast = useToast();
  const [meta, setMeta] = useState(null);
  const [server, setServer] = useState("");
  const [busy, setBusy] = useState(false);
  const [one, setOne] = useState({ level: "", pal: "", at_player: "" });
  // Passives live as an array for the picker and are joined to a comma string
  // only at submit time — that is the shape /admin/raids/spawn expects.
  const [passives, setPassives] = useState([]);
  const { pool, others } = usePalPool(server);
  const [tmplOver, setTmplOver] = useState({});
  // Presence gate state, per server. A manual spawn is NEVER blocked — firing
  // one by hand is how you test a fix — but PalDefender rejects every summon
  // until someone has loaded the world since the last restart (0 of 518
  // measured attempts succeeded on an empty world), so the admin gets told.
  const [presence, setPresence] = useState([]);

  useEffect(() => {
    api.get("/admin/raids/presence")
      .then(({ data }) => setPresence(data?.servers || []))
      .catch(() => setPresence([]));
  }, []);

  const serverState = presence.find((p) => p.server === server);
  const worldEmpty = serverState?.world_loaded_since_restart === false;

  const patchOne = (k, v) => setOne((o) => ({ ...o, [k]: v }));
  const patchTmpl = (k, v) =>
    setTmplOver((t) => {
      const next = { ...t };
      if (v === "" || v === null) delete next[k];
      else next[k] = v;
      return next;
    });

  const summonNow = async () => {
    if (!server) return toast.error("Pick a server first");
    setBusy(true);
    try {
      const q = new URLSearchParams({ server });
      if (one.level) q.set("level", one.level);
      if (one.pal) q.set("pal", one.pal);
      if (passives.length) q.set("passives", passives.join(","));
      if (one.at_player) q.set("at_player", one.at_player);
      const { data } = await api.post(
        `/admin/raids/spawn?${q.toString()}`,
        Object.keys(tmplOver).length ? tmplOver : {},
      );
      toast.success(`Summoned ${data.pal_name || "raid"} on ${server}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const overrideCount = Object.keys(tmplOver).length +
    Object.values(one).filter(Boolean).length + (passives.length ? 1 : 0);

  return (
    <div className="space-y-5" data-testid="admin-raids-manual">
      <header>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Swords size={17} className="text-[var(--teal)]" />
          Manual raids
        </h2>
        <p className="text-xs text-[var(--muted)] mt-1 max-w-3xl">
          Defaults for raids you fire yourself. Scheduled raids read their own
          per-server profile on the Automatic page, so changing anything here
          will not affect them.
        </p>
      </header>

      <RaidProfileEditor mode="manual" onLoaded={setMeta} />

      <section className="glass-card p-4 border border-[var(--teal)]/30"
        data-testid="summon-now-panel">
        <h4 className="text-xs uppercase tracking-wider text-[var(--bone)] mb-1 flex items-center gap-1.5">
          <Zap size={13} className="text-[var(--teal)]" /> Summon now
        </h4>
        <p className="text-[11px] text-[var(--muted)] mb-3">
          Everything blank falls back to the profile above.{" "}
          <span>
            Anything you set here applies to <strong>this one raid only</strong>{" "}
            and is never saved.
          </span>
        </p>

        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-[10px] text-[var(--muted)]">
            Server
            <select value={server} onChange={(e) => setServer(e.target.value)}
              className={`${NUM} w-56`} data-testid="summon-now-server">
              <option value="">Pick a server…</option>
              {(meta?.servers || []).map((s) => (
                <option key={s.name} value={s.name}>
                  {s.name} {s.modded ? "(modded)" : "(vanilla)"}
                </option>
              ))}
            </select>
          </label>
          {[["level", "Level", "profile"]].map(([key, label, ph]) => (
            <label key={key} className="text-[10px] text-[var(--muted)]">
              {label}
              <input type="number" min={1} max={255}
                value={one[key]} placeholder={ph}
                onChange={(e) => patchOne(key, e.target.value)}
                className={`${NUM} w-28`}
                data-testid={`summon-now-${key}`} />
            </label>
          ))}

          {/* Pal: the same custom listbox the old Quick Spawn card uses, so
              the posted value stays byte-identical to what /pool returned. */}
          <label className="text-[10px] text-[var(--muted)] flex-1 min-w-[200px] max-w-[320px]">
            Pal
            <div className="mt-0.5">
              <PalPicker value={one.pal} onChange={(v) => patchOne("pal", v)}
                pool={pool} others={others} slug="manual" disabled={!server} />
            </div>
          </label>

          <PlayerPicker server={server} value={one.at_player}
            onChange={(v) => patchOne("at_player", v)} />

          {/* Passives post as a comma string; the picker holds an array. */}
          <label className="text-[10px] text-[var(--muted)]">
            Passives
            <div className="mt-0.5">
              <PassivePicker selected={passives} onChange={setPassives} slug="manual" />
            </div>
          </label>
        </div>

        <p className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-3 mb-1">
          Override the boss for this raid
        </p>
        <div className="flex flex-wrap gap-3 items-end">
          {[["rank_hp", "Soul HP"], ["rank_attack", "Soul Atk"],
            ["trust", "Trust"], ["iv_cap", "IV cap"], ["soul_cap", "Soul cap"]].map(
            ([key, label]) => (
              <label key={key} className="text-[10px] text-[var(--muted)]">
                {label}
                <input type="number" min="0" value={tmplOver[key] ?? ""}
                  placeholder="profile"
                  onChange={(e) => patchTmpl(key, e.target.value === ""
                    ? "" : parseInt(e.target.value, 10) || 0)}
                  className={`${NUM} w-20`} data-testid={`summon-now-tmpl-${key}`} />
              </label>
            ),
          )}
          <label className="text-[10px] text-[var(--muted)]">
            Nickname
            <input type="text" maxLength={32} value={tmplOver.nick_name ?? ""}
              placeholder="profile"
              onChange={(e) => patchTmpl("nick_name", e.target.value)}
              className={`${NUM} w-32`} data-testid="summon-now-tmpl-nick" />
          </label>
        </div>

        {overrideCount > 0 && (
          <p className="mt-3 text-[11px] text-amber-300 flex gap-1.5"
            data-testid="summon-now-override-notice">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              {overrideCount} setting{overrideCount === 1 ? "" : "s"} overridden for
              this raid only — <strong>Save as defaults</strong> above is the only
              thing that changes the profile.
            </span>
          </p>
        )}

        {worldEmpty && (
          <p className="mt-3 text-[11px] text-red-300 flex gap-1.5"
            data-testid="summon-now-world-empty">
            <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
            <span>
              Nobody has loaded the world on <strong>{server}</strong> since it
              last restarted, so PalDefender has no PalID table yet and will
              reject the spawn. Firing anyway is fine — it will be recorded
              under Recent failures on the Automatic page.
            </span>
          </p>
        )}

        <button onClick={summonNow} disabled={busy || !server}
          className="btn btn-sm mt-3 flex items-center gap-1.5"
          data-testid="summon-with-these-settings">
          <Zap size={12} /> Summon with these settings
        </button>
      </section>
    </div>
  );
}
